import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileBarChart, TrendingUp, TrendingDown, CheckCircle2, Mail, Slack, ArrowRight, Zap } from 'lucide-react';

/* Performance Reporting — the 1-page weekly report, mocked live. */

const Grow = ({ children, i = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.5, delay: i * 0.1 }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function WeeklyReportShowcase({ lang }) {
  const es = lang === 'es';

  const kpis = [
    { t: es ? 'Ventas' : 'Sales', v: '$41,280', d: '+9%', up: true },
    { t: es ? 'Margen' : 'Margin', v: '31.2%', d: '+1.4', up: true },
    { t: 'Labor', v: '26.8%', d: '-0.6', up: true },
    { t: es ? 'Rating' : 'Rating', v: '4.7★', d: '+0.1', up: true },
    { t: es ? 'Directo' : 'Direct', v: '38%', d: '-2', up: false },
  ];

  return (
    <section className="py-20 lg:py-24 bg-neutral-950 text-white relative overflow-hidden" data-testid="showcase-performance-reporting">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 left-[15%] w-[600px] h-[420px] rounded-full blur-3xl opacity-[0.13]"
        style={{ background: 'radial-gradient(circle, #F77F11 0%, transparent 65%)' }}
      />
      <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <p className="kx-eyebrow-dark">{es ? 'Así se ve tu lunes' : 'What your Monday looks like'}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight">
            {es ? 'Una página. Cinco números. Una decisión.' : 'One page. Five numbers. One decision.'}
          </h2>
          <p className="mt-4 text-neutral-400 text-[15.5px] leading-relaxed">
            {es
              ? 'Cada lunes a las 8 AM recibes esto en tu email o Slack. Sin abrir dashboards, sin exportar nada. Lo lees en 3 minutos y sabes exactamente qué hacer esta semana.'
              : 'Every Monday at 8 AM this lands in your email or Slack. No dashboards to open, nothing to export. You read it in 3 minutes and know exactly what to do this week.'}
          </p>
          <div className="mt-5 flex items-center gap-2 text-[12px] text-neutral-400">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.05] border border-white/12 px-3 py-1.5 font-semibold"><Mail className="w-3.5 h-3.5" /> Email</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.05] border border-white/12 px-3 py-1.5 font-semibold"><Slack className="w-3.5 h-3.5" /> Slack</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.05] border border-white/12 px-3 py-1.5 font-semibold">WhatsApp</span>
          </div>
          <p className="mt-6 text-[13.5px] text-neutral-400">
            {es ? '¿Prefieres explorar los datos tú mismo? Mira nuestros ' : 'Prefer to explore the data yourself? See our '}
            <Link to="/services/dashboards" className="text-[#FFB672] hover:text-white font-semibold underline underline-offset-4 decoration-[#F77F11]/50" data-testid="report-showcase-link-dashboards">
              {es ? 'dashboards interactivos' : 'interactive dashboards'}
            </Link>
            {es ? ' por tipo de operación.' : ' by operation type.'}
          </p>
        </div>

        {/* The mock report */}
        <Grow i={1} className="lg:col-span-7">
          <div className="rounded-3xl bg-white text-neutral-900 p-6 sm:p-8 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)]">
            <div className="flex items-center justify-between border-b border-neutral-200 pb-4">
              <div className="flex items-center gap-2.5">
                <span className="inline-flex w-9 h-9 rounded-lg bg-[#F77F11] items-center justify-center">
                  <FileBarChart className="w-5 h-5 text-white" />
                </span>
                <div>
                  <p className="font-bold text-[15px] leading-tight">{es ? 'Reporte Semanal · Semana 24' : 'Weekly Ops Report · Week 24'}</p>
                  <p className="text-[11px] text-neutral-500">{es ? 'Lunes 8:00 AM · 1 página' : 'Monday 8:00 AM · 1 page'}</p>
                </div>
              </div>
              <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-neutral-400">KITXENS</span>
            </div>

            <div className="mt-5 grid grid-cols-5 gap-2">
              {kpis.map((k) => (
                <div key={k.t} className="rounded-xl bg-neutral-50 border border-neutral-200 p-2.5 text-center">
                  <p className="text-[9px] uppercase tracking-wider text-neutral-500 font-bold truncate">{k.t}</p>
                  <p className="mt-0.5 text-[13.5px] font-bold truncate">{k.v}</p>
                  <p className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${k.up ? 'text-emerald-600' : 'text-red-500'}`}>
                    {k.up ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}{k.d}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-xl bg-neutral-50 border border-neutral-200 p-4">
              <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-neutral-500">{es ? 'Qué cambió' : 'What changed'}</p>
              <p className="mt-1.5 text-[13px] text-neutral-700 leading-relaxed">
                {es
                  ? 'El brunch del sábado creció 22% tras la promo de Google Posts. El pedido directo cayó 2 pts: DoorDash lanzó promos agresivas en tu zona.'
                  : 'Saturday brunch grew 22% after the Google Posts promo. Direct ordering dropped 2 pts: DoorDash launched aggressive promos in your area.'}
              </p>
            </div>

            <div className="mt-3 rounded-xl bg-orange-50 border-l-4 border-[#F77F11] p-4">
              <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#F77F11] flex items-center gap-1.5">
                <Zap className="w-3 h-3" /> {es ? 'Acción recomendada' : 'Recommended action'}
              </p>
              <p className="mt-1.5 text-[13px] text-neutral-800 leading-relaxed font-medium">
                {es
                  ? 'Activar promo de pedido directo (envío gratis > $35) jueves–domingo. Impacto estimado: +$1,900/semana en margen.'
                  : 'Launch a direct-order promo (free delivery > $35) Thursday–Sunday. Estimated impact: +$1,900/week in margin.'}
              </p>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-600">
                <CheckCircle2 className="w-4 h-4" /> {es ? 'Implementamos por ti al aprobar' : 'We implement it for you on approval'}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-neutral-900 text-white text-[11px] font-bold px-3.5 py-1.5">
                {es ? 'Aprobar' : 'Approve'} <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </Grow>
      </div>
    </section>
  );
}
